import PropTypes from "prop-types";
import React from "react";
import { Link, useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";

import { fetcher } from "../../../apiHelpers";
import { API_ADMIN_ROUTES, API_ROUTES, PAGE_ROUTES } from "../../../constants";
import { appendParamsToUrl } from "../../../helpers";
import useUserStore from "../../../store/User.store";
import Icon from "../../Icon";
import { showErrorToast, showToast } from "../../Toast";
import TableMenu from "../TableMenu";

const isProdEnv = process.env.REACT_APP_ENV === `production`;

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "30%",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
};

const TableCellActions = ({ row }) => {
  const navigate = useNavigate();

  const user = useUserStore((state) => state.data.user);
  const token = user?.accessToken;
  const isProdEnv = process.env.REACT_APP_ENV === `production`;

  const [open, setOpen] = React.useState(false);
  const [transitionId, setTransitionId] = React.useState(null);
  const [comments, setComments] = React.useState(null);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleViewComment = async (transitionId) => {
    try {
      const res = await fetcher({
        url: appendParamsToUrl(API_ROUTES.PROPOSAL_COMMENT, {
          transitionId,
        }),
        token,
        method: "get",
      });
      setComments(JSON.parse(res?.comment));
      setTransitionId(transitionId);
      handleOpen();
    } catch (error) {
      console.error(error);
      showErrorToast(() => (
        <span>{`Error loading comment for transition ${transitionId}.`}</span>
      ));
    }
  };

  const handleLogoutUser = async (email = "") => {
    const data = {
      username: email,
    };
    try {
      await fetcher({
        url: API_ADMIN_ROUTES.ADMIN_LOGOUT,
        token,
        method: "POST",
        data,
      });
      showToast(() => (
        <span>{`${email} has been successfully logged out.`}</span>
      ));
    } catch (error) {
      console.warn(error);
      showErrorToast(() => <span>{`Error logging out ${email}.`}</span>);
    }
  };

  const handleDisableUser = async (email = "") => {
    const data = {
      username: email,
    };
    try {
      await fetcher({
        url: API_ADMIN_ROUTES.ADMIN_DISABLE,
        token,
        method: "POST",
        data,
      });
      showToast(() => (
        <span>{`${email} has been successfully disabled.`}</span>
      ));
    } catch (error) {
      console.warn(error);
      showErrorToast(() => <span>{`Error disabling ${email}.`}</span>);
    }
  };

  return (
    <>
      <TableMenu aria-label="Actions">
        {row?.actions?.map((action, index) => {
          return (
            {
              proposalForm: (
                <button
                  key={index}
                  onClick={() => {
                    navigate(
                      PAGE_ROUTES.proposal.getFullPath(action.transitionId)
                    );
                  }}
                >
                  <Icon name="system-list" />
                  View Proposal Form
                </button>
              ),
              duplicate: (
                <button
                  key={index}
                  onClick={() => {
                    navigate(
                      PAGE_ROUTES.duplicate.getFullPath(action.transitionId)
                    );
                  }}
                >
                  <Icon name="system-copy" />
                  Clone Proposal Form
                </button>
              ),
              outputReport: (
                <a
                  key={index}
                  href={PAGE_ROUTES.report.getFullPath(action.transitionId)}
                  target="_blank"
                  className={action.status !== "ready" ? "hidden" : ""}
                  rel="noreferrer"
                >
                  <Icon name="system-benchmark" />
                  View Output Report {action.reportId}
                </a>
              ),
              clientQuestionnaire: !isProdEnv && (
                <button
                  className={action.status !== "ready" ? "hidden" : ""}
                  key={index}
                  onClick={() => {
                    if (action.questionnaireId) {
                      navigate(
                        PAGE_ROUTES.clientQuestionnaire.getFullPath(
                          action.questionnaireId
                        )
                      );
                      return;
                    }
                    if (action.proposalId) {
                      navigate(
                        PAGE_ROUTES.clientQuestionnaireCreationWithProposal.getFullPath(
                          action.proposalId
                        )
                      );
                      return;
                    }

                    navigate(
                      PAGE_ROUTES.clientQuestionnaireCreation.getFullPath()
                    );
                  }}
                >
                  <Icon name="system-report" />
                  {action.questionnaireId
                    ? "View Client Questionnaire"
                    : "Start Client Questionnaire"}
                </button>
              ),
              disableUser: (
                <button
                  key={index}
                  onClick={() => {
                    handleDisableUser(action.email);
                  }}
                >
                  <Icon name="system-subtract-alt" />
                  Disable
                </button>
              ),
              logoutUser: (
                <button
                  key={index}
                  onClick={() => {
                    handleLogoutUser(action.email);
                  }}
                >
                  <Icon name="logout" />
                  Logout
                </button>
              ),
            }[action.action] || null
          );
        })}
      </TableMenu>
      <Modal
        variation="alert"
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Comment
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            Section: {comments ? Object.keys(comments)[0] : ""}
          </Typography>
          <Typography
            id="modal-modal-description"
            sx={{ mt: 2, fontStyle: "italic" }}
          >
            {comments ? comments[Object.keys(comments)[0]] : ""}
          </Typography>
          <Typography align="right" color={"blue"}>
            <Link to={"/proposals/" + transitionId}>Proposal Detail</Link>
          </Typography>
        </Box>
      </Modal>
    </>
  );
};

TableCellActions.propTypes = {
  row: PropTypes.object,
};

export default TableCellActions;
